import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Download, FileText, Filter, Camera, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';

// =====================================================================
// Consulta das Avaliações 360 gravadas.
//
// Layout no padrão do "Relatório de Auditoria de Segurança do Alimento"
// (Treinamento, RelatorioFormulario.js): uma LISTA PLANA — uma linha por visita —
// com filtros, exportação e paginação, e um botão de PDF por linha. Cada linha
// abre o DETALHE por área (que a 360 tem e o relatório da SA não). Substituiu a
// árvore Loja→Formulário→Avaliação a pedido da Julia (2026-09-22).
//
// Diferenças de conteúdo vs. a SA: a coluna de veredito da SA (APROVADA/IMPUR)
// aqui é a CLASSIFICAÇÃO por cor (Verde/Amarelo/Laranja/Vermelho) — a 360 não tem
// veredito; a nota é o percentual de conformidade.
// =====================================================================

const API_BASE = '/api/operacional/aval360';
const COR = '#475569'; // ardósia do módulo Operacional
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const CLASSIF_OPCOES = ['Verde', 'Amarelo', 'Laranja', 'Vermelho', 'NA'];

// ⚠️ Cores/rótulos da régua NOVA — espelha operacional/aval360/aval360-score.js.
const corClassificacao = (c) => {
  if (c === 'Verde')    return { bg: '#d1fae5', color: '#065f46', icone: '🟢', rotulo: 'Verde' };
  if (c === 'Amarelo')  return { bg: '#fef9c3', color: '#854d0e', icone: '🟡', rotulo: 'Amarelo' };
  if (c === 'Laranja')  return { bg: '#ffedd5', color: '#9a3412', icone: '🟠', rotulo: 'Laranja' };
  if (c === 'Vermelho') return { bg: '#fee2e2', color: '#991b1b', icone: '🔴', rotulo: 'Vermelho' };
  if (c === 'NA')       return { bg: '#f1f5f9', color: '#475569', icone: '⚪', rotulo: 'N/A' };
  return { bg: '#f1f5f9', color: '#475569', icone: '', rotulo: c || '-' };
};

const FAIXAS_LEGENDA = [
  { classif: 'Verde',    faixa: '90 a 100%' },
  { classif: 'Amarelo',  faixa: '80 a 89,99%' },
  { classif: 'Laranja',  faixa: '70 a 79,99%' },
  { classif: 'Vermelho', faixa: 'abaixo de 70%' },
];

const formatarDataBR = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};
const dataRef = (a) => String(a.DATA_AVALIACAO || a.DATA_CRIACAO || '').slice(0, 10);

const ITENS_POR_PAGINA = 25;

const Aval360Historico = ({ acoesExtras }) => {
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [detalheId, setDetalheId] = useState(null);
  const [abrindoPdf, setAbrindoPdf] = useState(null);

  // Filtros (client-side, instantâneos — a lista é pequena).
  const [fLoja, setFLoja] = useState('');
  const [fVersao, setFVersao] = useState('');
  const [fClassif, setFClassif] = useState('');
  const [fPeriodo, setFPeriodo] = useState(''); // mês de referência 'AAAA-MM' (input type=month)
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(`${API_BASE}/historico`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setAvaliacoes(await r.json());
    } catch (e) {
      setErro('Erro ao carregar as avaliações.');
      console.error(e);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const lojas = useMemo(() => [...new Set(avaliacoes.map((a) => a.LOJA).filter(Boolean))].sort(), [avaliacoes]);
  const versoes = useMemo(() => [...new Set(avaliacoes.map((a) => a.NOME_VERSAO).filter(Boolean))].sort(), [avaliacoes]);

  const filtradas = useMemo(() => avaliacoes.filter((a) => {
    if (fLoja && a.LOJA !== fLoja) return false;
    if (fVersao && a.NOME_VERSAO !== fVersao) return false;
    if (fClassif && a.CLASSIFICACAO !== fClassif) return false;
    const d = dataRef(a);
    // Período = mês de referência (AAAA-MM): compara só o prefixo ano-mês.
    if (fPeriodo && (!d || d.slice(0, 7) !== fPeriodo)) return false;
    return true;
  }), [avaliacoes, fLoja, fVersao, fClassif, fPeriodo]);

  // Volta para a 1ª página sempre que o filtro muda (senão fica numa página vazia).
  useEffect(() => { setPagina(1); }, [fLoja, fVersao, fClassif, fPeriodo]);

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / ITENS_POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const linhas = filtradas.slice((paginaAtual - 1) * ITENS_POR_PAGINA, paginaAtual * ITENS_POR_PAGINA);

  const temFiltro = fLoja || fVersao || fClassif || fPeriodo;
  const limparFiltros = () => { setFLoja(''); setFVersao(''); setFClassif(''); setFPeriodo(''); };

  // Abre o PDF numa aba nova. Passa por fetch (a rota exige token; <a href> daria 401).
  const abrirPdf = useCallback(async (id) => {
    setAbrindoPdf(id);
    try {
      const r = await fetch(`${API_BASE}/avaliacao/${id}?formato=pdf`, { headers: auth() });
      if (!r.ok) throw new Error('Não foi possível abrir o PDF desta avaliação.');
      const url = window.URL.createObjectURL(await r.blob());
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.message);
    } finally {
      setAbrindoPdf(null);
    }
  }, []);

  const exportarExcel = () => {
    const dados = filtradas.map((a) => ({
      'Avaliação': a.ID, 'Formulário': a.NOME_VERSAO, 'Loja': a.LOJA,
      'Data': formatarDataBR(a.DATA_AVALIACAO),
      'Avaliador': a.AVALIADOR_RESPONSAVEL || '', 'Gerente de Negócios': a.GERENTE_NEGOCIOS || '',
      'Gerente de Plantão': a.GERENTE_PLANTAO || '',
      'Sim': a.TOTAL_SIM, 'Não': a.TOTAL_NAO, 'N/A': a.TOTAL_NA, 'Percentual (%)': a.PERCENTUAL,
      'Classificação': corClassificacao(a.CLASSIFICACAO).rotulo,
      'Evidências (fotos)': a.QTD_EVIDENCIAS || 0,
      'Observações': a.OBSERVACAO_GERAL || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dados), 'Avaliações 360');
    XLSX.writeFile(wb, `Avaliacoes360_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (detalheId) return <DetalheAvaliacao id={detalheId} onVoltar={() => setDetalheId(null)} />;

  const th = { padding: '13px 16px', fontWeight: 700, borderBottom: '1px solid #e2e8f0', fontSize: 12.5, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
  const td = { padding: '13px 16px', color: '#334155', fontSize: 14, whiteSpace: 'nowrap' };
  const selStyle = { width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', outline: 'none', background: '#fff', color: '#475569', fontSize: 13 };

  return (
    <div>
      <style>{'.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}'}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, minWidth: 180, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Avaliações gravadas</h3>
        {acoesExtras}
        {filtradas.length > 0 && (
          <button onClick={exportarExcel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>
            <Download size={16} /> Exportar Excel
          </button>
        )}
      </div>

      {/* FILTROS (cartão, no padrão do relatório da SA) */}
      <div style={{ backgroundColor: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14, alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 170px' }}>
          <label style={filtroLbl}>Loja</label>
          <select value={fLoja} onChange={(e) => setFLoja(e.target.value)} style={selStyle}>
            <option value="">Todas</option>
            {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={filtroLbl}>Formulário</label>
          <select value={fVersao} onChange={(e) => setFVersao(e.target.value)} style={selStyle}>
            <option value="">Todos</option>
            {versoes.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={filtroLbl}>Classificação</label>
          <select value={fClassif} onChange={(e) => setFClassif(e.target.value)} style={selStyle}>
            <option value="">Todas</option>
            {CLASSIF_OPCOES.map((c) => <option key={c} value={c}>{corClassificacao(c).rotulo}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={filtroLbl}>Período (mm/aaaa)</label>
          <input type="month" value={fPeriodo} onChange={(e) => setFPeriodo(e.target.value)} style={selStyle} />
        </div>
        {temFiltro && (
          <button onClick={limparFiltros} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            <Filter size={15} /> Limpar
          </button>
        )}
      </div>

      {/* Legenda das faixas (régua nova) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 16, paddingLeft: 2 }}>
        <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}>Faixas (régua nova)</span>
        {FAIXAS_LEGENDA.map(({ classif, faixa }) => {
          const c = corClassificacao(classif);
          return (
            <span key={classif} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.bg, color: c.color, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
              {c.rotulo}<span style={{ fontWeight: 600, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>{faixa}</span>
            </span>
          );
        })}
      </div>

      {erro && <div style={alerta}>{erro}</div>}

      {/* LISTA PLANA */}
      <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 900 }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={th}>Loja</th>
                <th style={th}>Formulário</th>
                <th style={th}>Data</th>
                <th style={th}>G. Negócios</th>
                <th style={th}>G. Plantão</th>
                <th style={{ ...th, textAlign: 'right' }}>Nota</th>
                <th style={{ ...th, textAlign: 'center' }}>Classificação</th>
                <th style={{ ...th, textAlign: 'center' }}>Evidências</th>
                <th style={{ ...th, textAlign: 'center' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#64748b' }}>Carregando…</td></tr>}
              {!carregando && filtradas.length === 0 && (
                <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#64748b' }}>
                  {avaliacoes.length === 0 ? 'Nenhuma avaliação gravada ainda.' : 'Nenhuma avaliação para os filtros escolhidos.'}
                </td></tr>
              )}
              {linhas.map((a) => {
                const c = corClassificacao(a.CLASSIFICACAO);
                const aplic = (a.TOTAL_SIM || 0) + (a.TOTAL_NAO || 0);
                const n = Number(a.QTD_EVIDENCIAS || 0);
                return (
                  <tr key={a.ID} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                    onClick={() => setDetalheId(a.ID)}
                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                    <td style={{ ...td, fontWeight: 700, color: '#0f172a' }}>{a.LOJA}</td>
                    <td style={{ ...td, whiteSpace: 'normal', color: '#475569' }}>{a.NOME_VERSAO}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{formatarDataBR(a.DATA_AVALIACAO) || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{a.GERENTE_NEGOCIOS || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{a.GERENTE_PLANTAO || '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontWeight: 800, color: c.color, fontVariantNumeric: 'tabular-nums' }}>{a.PERCENTUAL}%</span>
                        <span style={{ fontSize: 11, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{a.TOTAL_SIM}/{aplic} conformes</span>
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 800, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>
                        {c.icone} {c.rotulo}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'center', color: n ? '#0f172a' : '#94a3b8' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}><Camera size={14} /> {n}</span>
                    </td>
                    {/* Ações não propagam o clique da linha (senão abririam o detalhe junto). */}
                    <td style={{ ...td, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button onClick={() => setDetalheId(a.ID)} title="Ver detalhe por área"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                          Detalhe
                        </button>
                        <button onClick={() => abrirPdf(a.ID)} disabled={abrindoPdf === a.ID} title="Abrir o PDF (com as fotos das perguntas Não)"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 6, border: `1px solid ${COR}`, background: '#fff', color: COR, fontWeight: 700, fontSize: 12, cursor: abrindoPdf === a.ID ? 'wait' : 'pointer' }}>
                          {abrindoPdf === a.ID ? <Loader2 size={13} className="spin" /> : <FileText size={13} />}
                          PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Rodapé de paginação */}
        <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Página {paginaAtual} de {totalPaginas}
            <span style={{ marginLeft: 10, fontWeight: 600 }}>(Total: {filtradas.length} avaliaç{filtradas.length === 1 ? 'ão' : 'ões'})</span>
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setPagina((p) => Math.max(p - 1, 1))} disabled={paginaAtual === 1}
              style={btnPag(paginaAtual === 1)}>Anterior</button>
            <button onClick={() => setPagina((p) => Math.min(p + 1, totalPaginas))} disabled={paginaAtual === totalPaginas}
              style={btnPag(paginaAtual === totalPaginas)}>Próxima</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const filtroLbl = { display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 };
const btnPag = (desab) => ({
  padding: '6px 14px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: '#fff',
  color: desab ? '#94a3b8' : '#0f172a', cursor: desab ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
});

// ---------------------------------------------------------------------
// Detalhe (mesmo arquivo p/ reduzir fragmentação)
// ---------------------------------------------------------------------
const DetalheAvaliacao = ({ id, onVoltar }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/avaliacao/${id}`, { headers: auth() });
        const data = await r.json();
        if (!r.ok) throw new Error(data?.message || 'Erro');
        setDados(data);
      } catch (e) { setErro(e.message); }
    })();
  }, [id]);

  const baixar = async (formato, ext) => {
    try {
      const r = await fetch(`${API_BASE}/avaliacao/${id}?formato=${formato}`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Avaliacao360_${id}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErro(`Erro ao exportar ${ext.toUpperCase()}: ` + e.message); }
  };

  if (erro) return (
    <div>
      <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
      <div style={{ ...alerta, marginTop: 12 }}>{erro}</div>
    </div>
  );
  if (!dados) return <p style={{ color: '#64748b' }}>Carregando…</p>;

  const { avaliacao, respostas, resumoPorArea = [] } = dados;
  const c = corClassificacao(avaliacao.CLASSIFICACAO);
  const aplicGeral = (avaliacao.TOTAL_SIM || 0) + (avaliacao.TOTAL_NAO || 0);

  const porAreaId = new Map(resumoPorArea.map((a) => [a.idArea, []]));
  for (const r of respostas) {
    if (!porAreaId.has(r.ID_AREA)) porAreaId.set(r.ID_AREA, []);
    porAreaId.get(r.ID_AREA).push(r);
  }

  const rotuloResp = (r) => (r === 'NA' ? 'N/A' : (r === 'SIM' ? 'Sim' : 'Não'));
  const corResp = (r) => (r === 'NA' ? '#64748b' : (r === 'SIM' ? '#059669' : '#dc2626'));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <h3 style={{ margin: 0, flex: 1, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Avaliação 360 #{avaliacao.ID} — {avaliacao.LOJA} · {formatarDataBR(avaliacao.DATA_AVALIACAO)}
        </h3>
        <button onClick={() => baixar('pdf', 'pdf')} style={btnVermelho}>
          <FileText size={16} /> Exportar PDF
        </button>
        <button onClick={() => baixar('xlsx', 'xlsx')} style={btnVerde}>
          <Download size={16} /> Exportar XLSX
        </button>
      </div>

      <div style={{
        background: c.bg, border: `2px solid ${c.color}`,
        borderRadius: 12, padding: 16, marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 28 }}>{c.icone}</div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', letterSpacing: 0.5 }}>RESULTADO GERAL</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>
            {avaliacao.TOTAL_SIM}/{aplicGeral} conformes · {avaliacao.PERCENTUAL}% · {c.rotulo}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#475569' }}>
          <div>Formulário: <strong>{avaliacao.NOME_VERSAO}</strong></div>
          <div>Avaliador: <strong>{avaliacao.AVALIADOR_RESPONSAVEL || '-'}</strong></div>
          <div>G. Negócios: <strong>{avaliacao.GERENTE_NEGOCIOS || '-'}</strong></div>
          <div>G. Plantão: <strong>{avaliacao.GERENTE_PLANTAO || '-'}</strong></div>
        </div>
      </div>

      {/* Percentual por área (os 6 indicadores da 360) */}
      {resumoPorArea.length > 0 && (
        <div style={card}>
          <h4 style={{ margin: '0 0 12px', color: '#0f172a' }}>Percentual por Área</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#475569' }}>
              <tr>
                {['Área', 'Conformes', '%', 'Classificação'].map((t, i) => (
                  <th key={t} style={{ ...thD, color: '#fff', borderBottom: 'none', textAlign: i === 0 ? 'left' : 'center' }}>{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resumoPorArea.map((a) => {
                const ca = corClassificacao(a.classificacao);
                const aplic = a.totalSim + a.totalNao;
                return (
                  <tr key={a.idArea} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={tdD}>{a.codigoArea ? <span style={{ color: '#64748b', fontWeight: 700 }}>{a.codigoArea} · </span> : null}{a.area}</td>
                    <td style={{ ...tdD, textAlign: 'center', fontWeight: 700 }}>{a.classificacao === 'NA' ? '—' : `${a.totalSim}/${aplic}`}</td>
                    <td style={{ ...tdD, textAlign: 'center' }}>{a.classificacao === 'NA' ? '—' : `${Math.round(a.percentual)}%`}</td>
                    <td style={{ ...tdD, textAlign: 'center', background: ca.bg, color: ca.color, fontWeight: 700 }}>
                      {ca.icone} {ca.rotulo}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '2px solid #475569' }}>
                <td style={{ ...tdD, fontWeight: 800 }}>RESULTADO GERAL</td>
                <td style={{ ...tdD, textAlign: 'center', fontWeight: 800 }}>{avaliacao.TOTAL_SIM}/{aplicGeral}</td>
                <td style={{ ...tdD, textAlign: 'center', fontWeight: 800 }}>{Math.round(avaliacao.PERCENTUAL)}%</td>
                <td style={{ ...tdD, textAlign: 'center', background: c.bg, color: c.color, fontWeight: 800 }}>
                  {c.icone} {c.rotulo}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Respostas por área */}
      {[...porAreaId.entries()].map(([idArea, perguntas]) => {
        const resumo = resumoPorArea.find((a) => a.idArea === idArea);
        const nomeArea = resumo ? `${resumo.codigoArea ? resumo.codigoArea + ' · ' : ''}${resumo.area}` : (perguntas[0]?.AREA || '');
        let subAtual = null;
        return (
          <div key={idArea} style={card}>
            <h4 style={{ margin: '0 0 12px', color: '#0f172a' }}>{nomeArea}</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={thD}>Pergunta</th>
                  <th style={{ ...thD, width: 80, textAlign: 'center' }}>Resp.</th>
                </tr>
              </thead>
              <tbody>
                {perguntas.map((p) => {
                  let sub = null;
                  if (p.SUBSECAO && p.SUBSECAO !== subAtual) {
                    subAtual = p.SUBSECAO;
                    sub = (
                      <tr key={`sub-${p.ID_PERGUNTA}`} style={{ background: '#f1f5f9' }}>
                        <td style={{ ...tdD, fontWeight: 800, color: '#334155' }} colSpan={2}>{p.SUBSECAO}</td>
                      </tr>
                    );
                  }
                  return (
                    <React.Fragment key={p.ID_PERGUNTA}>
                      {sub}
                      <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={tdD}>
                          {/* Sequencial estilo PDF (QSL-01), DERIVADO de área+ORDEM — não é o código opaco. */}
                          <span style={{ fontWeight: 700, color: '#64748b', marginRight: 6 }}>
                            {p.CODIGO_AREA}-{String(p.ORDEM).padStart(2, '0')}
                          </span>
                          {p.TEXTO}
                          {p.RESPOSTA === 'NAO' && p.OBSERVACAO && (
                            <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}><strong>Obs.:</strong> {p.OBSERVACAO}</div>
                          )}
                          {p.RESPOSTA === 'NAO' && (p.FOTOS || []).length > 0 && (
                            <div style={{ marginTop: 2, fontSize: 11, color: '#94a3b8' }}>{(p.FOTOS || []).length} foto(s) de evidência (ver PDF)</div>
                          )}
                        </td>
                        <td style={{ ...tdD, textAlign: 'center', fontWeight: 700, color: corResp(p.RESPOSTA) }}>
                          {rotuloResp(p.RESPOSTA)}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      {avaliacao.OBSERVACAO_GERAL && (
        <div style={card}>
          <h4 style={{ margin: '0 0 8px', color: '#0f172a' }}>Observações gerais</h4>
          <p style={{ margin: 0, color: '#475569', whiteSpace: 'pre-wrap' }}>{avaliacao.OBSERVACAO_GERAL}</p>
        </div>
      )}
    </div>
  );
};

const btnVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 };
const alerta = { padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 };
const thD = { padding: 10, textAlign: 'left', fontWeight: 800, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
const tdD = { padding: 10, color: '#0f172a' };
const btnVerde = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
  background: '#059669', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};
const btnVermelho = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
  background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};

export default Aval360Historico;
